// src/App.js
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthState } from './hooks/useAuthState';
import { auth } from './firebase';

// Components
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import EditEventPage from './pages/EditEventPage';
import Dashboard from './components/Dashboard';
import AddNewEvent from './components/AddNewEvent';
import ExistingEvents from './components/ExistingEvents';
import ViewRegistrations from './components/ViewRegistrations';
import NotificationManager from './components/NotificationManager';

// Styles
import './App.css';

/**
 * Admin App Component
 * 
 * Main application component for the YouthXConnect Events Admin Panel.
 * Enhanced with comprehensive notification management system and professional UI.
 * 
 * Key Features:
 * - Firebase authentication integration
 * - Professional admin dashboard
 * - Event management capabilities  
 * - Enhanced notification system for user communication
 * - Registration management and analytics
 * - Responsive design optimized for admin workflows
 * 
 * Security Features:
 * - Authentication required for all admin routes
 * - Role-based access control
 * - Secure Firebase configuration
 * 
 * @returns {JSX.Element} The admin application
 */
function App() {
  // Authentication state management
  const [user, loading, error] = useAuthState(auth);
  
  // Global UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /**
   * Loading state during authentication check
   */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  /**
   * Authentication error state
   */
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Authentication Error</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /**
   * Unauthenticated state - show login
   */
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Login />
      </div>
    );
  }

  /**
   * Authenticated admin interface
   */
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Main Admin Dashboard */}
          <Route 
            path="/" 
            element={
              <AdminDashboard 
                sidebarOpen={sidebarOpen} 
                setSidebarOpen={setSidebarOpen}
              />
            } 
          />
          
          {/* Dashboard Analytics */}
          <Route 
            path="/dashboard" 
            element={
              <AdminDashboard 
                sidebarOpen={sidebarOpen} 
                setSidebarOpen={setSidebarOpen}
                activeComponent={<Dashboard />}
              />
            } 
          />
          
          {/* Event Management */}
          <Route 
            path="/add-event" 
            element={
              <AdminDashboard 
                sidebarOpen={sidebarOpen} 
                setSidebarOpen={setSidebarOpen}
                activeComponent={<AddNewEvent />}
              />
            } 
          />
          <Route 
            path="/events" 
            element={
              <AdminDashboard 
                sidebarOpen={sidebarOpen} 
                setSidebarOpen={setSidebarOpen}
                activeComponent={<ExistingEvents />}
              />
            } 
          />
          <Route 
            path="/edit-event/:id" 
            element={<EditEventPage />} 
          />
          
          {/* Registration Management */}
          <Route 
            path="/registrations" 
            element={
              <AdminDashboard 
                sidebarOpen={sidebarOpen} 
                setSidebarOpen={setSidebarOpen}
                activeComponent={<ViewRegistrations />}
              />
            } 
          />
          
          {/* Notification Management - NEW FEATURE */}
          <Route 
            path="/notifications" 
            element={
              <AdminDashboard 
                sidebarOpen={sidebarOpen} 
                setSidebarOpen={setSidebarOpen}
                activeComponent={<NotificationManager />}
              />
            } 
          />
          
          {/* Individual Login Route (fallback) */}
          <Route path="/login" element={<Login />} />
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;